import React, { useState } from 'react';
import { 
  Card, CardContent, Box, Typography, Grid, Button, Paper,
  Chip, Rating, Divider, Dialog, DialogContent, IconButton, DialogTitle
} from '@mui/material';
import { 
  Preview as PreviewIcon,
  Save as SaveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ZoomIn as ZoomInIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import LiveTradingButton from '../LiveTradingButton';

const AITradePreview = ({ tradeData, previewUrl, metrics, onSave }) => {
  const [zoomOpen, setZoomOpen] = useState(false);
  const getPnLColor = (value) => {
    const num = parseFloat(value);
    if (num > 0) return 'success.main';
    if (num < 0) return 'error.main';
    return 'text.secondary';
  };

  const getPnLIcon = (value) => {
    const num = parseFloat(value);
    if (num > 0) return <TrendingUpIcon />;
    if (num < 0) return <TrendingDownIcon />;
    return null;
  };

  return (
    <Card elevation={3}>
      <CardContent sx={{ p: { xs: 0, sm: 0 } }}>
        <Box sx={{ 
          p: { xs: 2, sm: 3 }, 
          bgcolor: 'success.main', 
          color: 'success.contrastText',
          textAlign: 'center'
        }}>
          <PreviewIcon sx={{ fontSize: { xs: 36, sm: 48 }, mb: 1 }} />
          <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
            Trade Preview
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
            Review your trade before saving
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          <Grid container spacing={{ xs: 2, sm: 4 }}>
            {/* Screenshot Preview */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  📊 Chart Screenshot
                </Typography>
                {previewUrl && (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <img
                      src={previewUrl}
                      alt="Trade Screenshot"
                      style={{
                        maxWidth: '100%',
                        maxHeight: 300,
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        cursor: 'pointer'
                      }}
                      onClick={() => setZoomOpen(true)}
                    />
                    <IconButton 
                      size="small"
                      onClick={() => setZoomOpen(true)}
                      sx={{ 
                        position: 'absolute', 
                        bottom: 8, 
                        right: 8,
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        color: 'white',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' }
                      }}
                    >
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Trade Summary */}
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom color="primary" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  📋 Trade Summary
                </Typography>
                
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h4" fontWeight="bold" color="primary" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                    {tradeData.symbol || 'N/A'}
                  </Typography>
                  <Chip 
                    label={tradeData.category || 'Stock'} 
                    size="small" 
                    color="primary" 
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Entry Price
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      ${tradeData.entryPrice || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Exit Price
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      ${tradeData.exitPrice || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Stop Loss
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      ${tradeData.stopLoss || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Position Size
                    </Typography>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      ${tradeData.positionSize || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Trade Rating
                  </Typography>
                  <Rating value={tradeData.rating || 3} readOnly />
                </Box>

                {tradeData.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                      {tradeData.notes}
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Risk/Reward Analysis */}
            {metrics?.riskRewardRatio && (
              <Grid item xs={12}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: { xs: 2, sm: 3 }, 
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    📈 Risk/Reward Analysis
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        R:R Ratio
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        {metrics.riskRewardRatioFormatted || '1:1'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Risk Amount
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        ${metrics.riskAmount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Reward Amount
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        ${metrics.rewardAmount}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Quantity
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        {metrics.quantity}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  startIcon={<SaveIcon />}
                  onClick={onSave}
                  sx={{ 
                    py: { xs: 1, sm: 1.5 }, 
                    px: { xs: 3, sm: 4 },
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  Save Trade
                </Button>
                <LiveTradingButton 
                  tradeData={tradeData}
                  onSuccess={(result) => {
                    console.log('Live trade executed:', result);
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </CardContent>

      {/* Zoom Dialog */}
      <Dialog 
        open={zoomOpen} 
        onClose={() => setZoomOpen(false)} 
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'primary.main',
          color: 'white'
        }}>
          <Typography variant="h6">Chart Screenshot</Typography>
          <IconButton onClick={() => setZoomOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, textAlign: 'center' }}>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Trade Screenshot"
              style={{
                maxWidth: '100%',
                maxHeight: '80vh',
                borderRadius: 4
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default AITradePreview;